import { ChangeEvent, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { CAST_AI } from '@cast/constants';
import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import {
  ActionsGroup,
  Alert,
  Button,
  DialogV2,
  Divider,
  HexIcon,
  Icons,
  Indicator,
  Input,
  Inset,
  LinkBehaviour,
  useCloseDialog,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { OrganizationAvatar } from '@cast/shared-organization-ui';
import { isDemoCluster, openChat } from '@cast/utils';

import { Link } from 'components/router';
import {
  useNavigate,
  ORGANIZATIONS_QUERY_KEY,
} from '@cast/infrastructure-organization';

import { useDeleteChildOrganizationMutation } from '../../../../../hooks/mutations/enterprise';
import { useDeleteOrganizationMutation } from '../../../../../hooks/mutations/organization';
import { getChildOrganizationsQueryKey } from '../../../../../hooks/queries/enterprise';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { useClustersQuery } from '@cast/shared-cluster-context-data-access';

export type DeleteChildOrganizationDialogProps = {
  organization: { id: string; name: string };
};

export const DeleteChildOrganizationDialogContent = ({
  organization,
}: DeleteChildOrganizationDialogProps) => {
  const closeDialog = useCloseDialog();
  const queryClient = useQueryClient();
  const [isEnabled, setIsEnabled] = useState(false);
  const { enterpriseOrgId, setCurrentOrganization, organizations } =
    useOrganizations();

  // Detect if organization is a child org or standalone
  // Use enterpriseOrgId presence to detect context - if we have it, we're deleting a child org
  const isChildOrg = !!enterpriseOrgId;

  const childOrganizations = organizations?.filter(
    (org) => org.parentId === enterpriseOrgId && org.id !== organization.id
  );
  const navigate = useNavigate();

  // Use appropriate mutation based on org type
  const childOrgMutation = useDeleteChildOrganizationMutation();
  const standaloneOrgMutation = useDeleteOrganizationMutation();

  const { mutate, isPending, isSuccess, isError } = isChildOrg
    ? childOrgMutation
    : standaloneOrgMutation;

  // Fetch clusters to check if org has any
  const { clusters } = useClustersQuery({
    organizationId: organization.id,
    enabled: !isSuccess && !isError,
    skipMiddleware: true,
  });

  const handleClose = () => {
    if (isSuccess) {
      if (isChildOrg && childOrganizations?.[0]) {
        // Child org: Navigate to sibling child org
        setCurrentOrganization(childOrganizations[0].id, {
          skipRedirect: true,
        });
        navigate(
          `/organization/enterprise/${enterpriseOrgId}/child-organizations?org=${childOrganizations[0].id}`
        );
      } else if (!isChildOrg) {
        // Standalone org: Find another org to switch to
        const otherOrgs = organizations?.filter(
          (org) => org.id !== organization.id
        );
        if (otherOrgs?.[0]) {
          setCurrentOrganization(otherOrgs[0].id, {
            skipRedirect: false,
          });
        }
      }

      queryClient.invalidateQueries({
        queryKey: [ORGANIZATIONS_QUERY_KEY],
      });

      // Only invalidate enterprise children cache when deleting a child org
      if (isChildOrg && enterpriseOrgId) {
        queryClient.invalidateQueries({
          queryKey: getChildOrganizationsQueryKey({
            enterpriseOrgId,
          }),
        });
      }
    }
    closeDialog();
  };

  // Filter out demo clusters - they should not prevent deletion
  const realClusters =
    clusters?.filter((cluster) => !isDemoCluster(cluster.id)) ?? [];
  const hasClusters = realClusters.length > 0;
  const icon = isSuccess ? <Icons.CheckCircle /> : <Icons.XCircle />;
  const iconColor = isSuccess
    ? tokens.theme.fg.status.onSuccessSubtle
    : tokens.theme.fg.status.onErrorSubtle;
  const hexColor = isSuccess
    ? tokens.theme.bg.status.successSubtle
    : tokens.theme.bg.status.errorSubtle;

  const handleChange = (
    _: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    value: string
  ) => {
    setIsEnabled(organization.name?.trim() === value?.trim());
  };

  return (
    <DialogV2
      testId="delete-child-organization-dialog"
      title="Delete organization"
      metadata={
        <Stack direction="row" gap={6}>
          <OrganizationAvatar organizationId={organization.id} size="xs" />
          <Typography variant="A1" color={tokens.theme.fg.structural.primary}>
            {organization.name}
          </Typography>
        </Stack>
      }
      footerDivider
      endActions={
        !(isSuccess || isError) ? (
          hasClusters ? (
            <Button
              testId="delete-child-organization-close-button"
              variant="secondary"
              onClick={() => closeDialog()}
            >
              Close
            </Button>
          ) : (
            <ActionsGroup>
              <Button
                testId="delete-child-organization-close-button"
                variant="secondary"
                onClick={() => closeDialog()}
              >
                Cancel
              </Button>
              <Button
                testId="delete-child-organization-button"
                variant="danger"
                onClick={() => mutate(organization.id)}
                disabled={!isEnabled}
                loading={isPending}
              >
                Delete
              </Button>
            </ActionsGroup>
          )
        ) : isSuccess ? (
          <Button
            testId="delete-child-organization-done-button"
            variant="primary"
            onClick={() => handleClose()}
          >
            Done
          </Button>
        ) : (
          <ActionsGroup>
            <Button
              testId="delete-child-organization-close-button"
              variant="secondary"
              onClick={() => closeDialog()}
            >
              Close
            </Button>
            <Button
              testId="delete-child-organization-retry-button"
              variant="primary"
              onClick={() => mutate(organization.id)}
            >
              Retry
            </Button>
          </ActionsGroup>
        )
      }
    >
      <Inset pb={24}>
        {!(isSuccess || isError) && (
          <>
            {hasClusters && (
              <Stack>
                <Stack direction="row" gap={12}>
                  <Stack>
                    <Indicator
                      size="small"
                      color="grey"
                      count={1}
                      sx={{ mt: 3, maxWidth: '16px' }}
                    />
                    <Divider orientation="vertical" sx={{ mr: 7 }} />
                  </Stack>
                  <Stack>
                    <Stack>
                      <Typography
                        variant="A1"
                        color={tokens.theme.fg.structural.primary}
                      >
                        Disconnect clusters
                      </Typography>
                      <Typography
                        variant="P12R"
                        color={tokens.theme.fg.structural.secondary}
                        mb={16}
                      >
                        You must disconnect all clusters and databases from the
                        organization before you can delete it.
                      </Typography>
                      <Button
                        data-testid="delete-child-organization-cluster-list-button"
                        variant="primary"
                        endIcon={<Icons.ArrowSquareOut />}
                        sx={{ mb: 32 }}
                        component={Link}
                        rel="noopener noreferrer"
                        target="_blank"
                        to={`${PRODUCT_BASE_PATHS.automation}/cluster-list?org=${organization.id}`}
                      >
                        Go to cluster list
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
                <Stack direction="row" gap={12}>
                  <Stack>
                    <Indicator
                      size="small"
                      color="grey"
                      count={2}
                      sx={{ mt: 3, maxWidth: '16px' }}
                    />
                  </Stack>
                  <Stack>
                    <Stack>
                      <Typography
                        variant="A1"
                        color={tokens.theme.fg.structural.primary}
                      >
                        Delete organization
                      </Typography>
                      <Typography
                        variant="P12R"
                        color={tokens.theme.fg.structural.secondary}
                      >
                        Once all resources are disconnected, you can permanently
                        delete the organization. This action cannot be undone.
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            )}
            {!hasClusters && (
              <>
                <Typography
                  variant="A1"
                  color={tokens.theme.fg.structural.primary}
                  mb={4}
                >
                  Confirmation
                </Typography>
                <Typography
                  variant="P12R"
                  color={tokens.theme.fg.structural.secondary}
                  mb={16}
                >
                  Please confirm you want to delete your organization by
                  entering its name below.
                </Typography>
                <Stack gap={8}>
                  <Box
                    sx={{
                      backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
                      color: tokens.theme.fg.structural.secondary,
                      padding: '0 12px',
                      lineHeight: '30px',
                      borderColor: tokens.theme.border.structural.neutral,
                      borderRadius: '4px',
                    }}
                  >
                    {organization.name}
                  </Box>
                  <Input
                    testId="delete-child-organization-input"
                    onChange={handleChange}
                    placeholder="Re-enter organization name here to confirm"
                  />
                </Stack>
                <Alert color="error" icon="warning" sx={{ marginTop: '28px' }}>
                  All information will be permanently deleted and cannot be
                  recovered. Users who only belong to this organization will
                  lose all access to {organization.name}.
                </Alert>
              </>
            )}
          </>
        )}
        {(isSuccess || isError) && (
          <Stack>
            <Stack direction="row" gap="12px" alignItems="center">
              <HexIcon
                hexColor={hexColor}
                iconColor={iconColor}
                icon={icon}
                size={36}
              />
              <Typography
                variant="h5"
                color={tokens.theme.fg.structural.primary}
                data-testid="delete-child-organization-result-title"
              >
                {isSuccess
                  ? 'Organization deleted successfully'
                  : 'Failed to delete organization'}
              </Typography>
            </Stack>
            <Box pl={48}>
              {isSuccess && (
                <Typography
                  variant="P12R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  Organization {organization.name} has been permanently removed.
                  Create a new organization and invite your team to continue
                  using {CAST_AI}.
                </Typography>
              )}
              {isError && (
                <Typography
                  variant="P14R"
                  color={tokens.theme.fg.structural.secondary}
                  component="div"
                >
                  The deletion process encountered an error. Try again or
                  contact our{' '}
                  <LinkBehaviour
                    testId="delete-child-organization-support-team-chat"
                    component="span"
                    onClick={openChat}
                  >
                    support team
                  </LinkBehaviour>{' '}
                  if you continue to experience problems.
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </Inset>
    </DialogV2>
  );
};
